<template>
    <div class="contents">
        <div
            v-if="isDocked"
            class="nfhs-reader-dock-wrap pointer-events-auto fixed"
            :style="dockStyle"
        >
            <button
                type="button"
                class="nfhs-reader-dock relative h-full w-full overflow-hidden rounded-xl bg-white shadow-2xl ring-1 ring-slate-200"
                :class="{
                    'nfhs-reader-dock--floating': reader.dockFloating || isDockDragging,
                }"
                title="Drag to reposition · Click to expand"
                aria-label="Expand publication reader"
                @pointerdown="onDockPointerDown"
            >
                <img
                    class="h-[120px] w-[80px] object-cover"
                    draggable="false"
                    :src="coverSrc"
                    :alt="title"
                    @error="onCoverError"
                >
                <span class="absolute inset-x-0 bottom-0 bg-slate-900/70 px-1.5 py-1 text-[10px] font-semibold leading-tight text-white">
                    {{ title }}
                </span>
            </button>
            <button
                type="button"
                class="nfhs-reader-dock-close"
                title="Close reader"
                aria-label="Close reader"
                @pointerdown.stop
                @click.stop="mainStore.closeNfhsDigitalReader(reader.id)"
            >
                <font-awesome-icon icon="fa-solid fa-xmark" class="h-3 w-3" />
            </button>
        </div>

        <div
            v-else
            class="nfhs-preview-panel nfhs-preview-panel--compact pointer-events-auto fixed flex flex-col overflow-hidden bg-white shadow-2xl ring-1 ring-slate-200"
            :class="reader.maximized ? 'nfhs-preview-panel--maximized rounded-xl' : 'rounded-2xl'"
            :style="panelStyle"
            role="dialog"
            aria-modal="false"
            :aria-label="eyebrow + ' — ' + title"
            @pointerdown.capture="onPanelPointerDown"
        >
            <header
                class="nfhs-reader-chrome flex cursor-move select-none items-center justify-between gap-3 border-b border-slate-100 bg-white"
                :class="reader.maximized ? 'px-3 py-1.5' : 'px-3 py-2 sm:px-5 sm:py-2.5'"
                style="touch-action: none"
                @pointerdown="onMoveStart"
                @dblclick="onHeaderDoubleClick"
            >
                <div class="min-w-0">
                    <p class="hidden text-[10px] font-semibold uppercase tracking-[0.14em] text-teal-700/80 sm:block">
                        {{ eyebrow }}
                    </p>
                    <h3
                        class="truncate font-semibold text-slate-900"
                        :class="reader.maximized ? 'text-sm' : 'text-sm sm:mt-0.5 sm:text-base'"
                    >
                        {{ title }}
                    </h3>
                </div>
                <div class="flex shrink-0 items-center gap-0.5" @pointerdown.stop>
                    <button
                        v-if="reader.mode === 'read'"
                        type="button"
                        class="rounded-lg p-2 text-slate-400 transition hover:bg-slate-50 hover:text-teal-700"
                        title="Collapse to thumbnail (unloads NFHS until you expand it again)"
                        @click="mainStore.collapseNfhsDigitalReader(reader.id)"
                    >
                        <font-awesome-icon icon="fa-solid fa-compress" class="h-4 w-4" />
                    </button>
                    <button
                        type="button"
                        class="rounded-lg p-2 text-slate-400 transition hover:bg-slate-50 hover:text-teal-700"
                        :title="reader.maximized ? 'Restore size' : 'Fill the screen'"
                        @click="mainStore.maximizeNfhsDigitalReader(reader.id)"
                    >
                        <font-awesome-icon
                            :icon="reader.maximized ? 'fa-regular fa-window-restore' : 'fa-solid fa-expand'"
                            class="h-4 w-4"
                        />
                    </button>
                    <a
                        v-if="readerUrl"
                        class="rounded-lg p-2 text-slate-400 transition hover:bg-slate-50 hover:text-teal-700"
                        :href="readerUrl"
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Open in new tab"
                        draggable="true"
                        @click.stop
                    >
                        <font-awesome-icon icon="fa-solid fa-arrow-up-right-from-square" class="h-4 w-4" />
                    </a>
                    <button
                        type="button"
                        class="rounded-lg p-2 text-slate-400 transition hover:bg-slate-50 hover:text-slate-700"
                        title="Close reader (unloads NFHS — you may need to sign in again)"
                        @click="mainStore.closeNfhsDigitalReader(reader.id)"
                    >
                        <font-awesome-icon icon="fa-solid fa-xmark" class="h-4 w-4" />
                    </button>
                </div>
            </header>

            <div
                :id="`nfhs-reader-body-${reader.id}`"
                class="relative min-h-0 flex-1 bg-white"
                @mousedown.stop
            >
                <iframe
                    v-if="readerUrl"
                    ref="nfhsFrame"
                    :key="`${reader.mode}-${reader.id}`"
                    :src="frameSrc"
                    :title="iframeTitle"
                    class="nfhs-reader-iframe h-full w-full border-0 bg-white"
                    :class="{ invisible: !frameReady }"
                    referrerpolicy="origin"
                    :sandbox="iframeSandbox"
                    allow="fullscreen; clipboard-read; clipboard-write"
                    allowfullscreen
                    loading="eager"
                    @load="onIframeLoad"
                ></iframe>
                <div
                    v-show="isInteracting"
                    class="nfhs-preview-interaction-shield absolute inset-0 z-[3]"
                ></div>
            </div>

            <template v-if="!reader.maximized">
                <div
                    v-for="handle in resizeHandles"
                    :key="handle"
                    class="nfhs-preview-resize-handle"
                    :class="`nfhs-preview-resize-handle--${handle}`"
                    @pointerdown.stop.prevent="onResizeStart($event, handle)"
                ></div>
            </template>
        </div>
    </div>
</template>

<script>
import { mapStores } from 'pinia'
import useMainStore from '@/stores/main'
import {
    NFHS_DEFAULT_COVER,
    NFHS_DOCK_WIDTH,
    NFHS_DOCK_Z_BASE,
    NFHS_INSET,
    NFHS_PANEL_Z_BASE,
    nfhsIframeHistorySeed,
    nfhsOpenReaderUrl,
    nfhsReaderCover,
    nfhsReaderTitle,
} from '@/utils/nfhsDigital'
import {
    NFHS_READER_ORIGIN,
    clearNfhsEmbedLogoutPending,
    isNfhsReaderMessage,
    nfhsEmbedLogoutIsPending,
    postNfhsEmbedLogout,
} from '@/utils/nfhsEmbedBridge'

// No allow-top-navigation: a click inside NFHS must not move the learnapp window.
const NFHS_IFRAME_SANDBOX = [
    'allow-downloads',
    'allow-forms',
    'allow-modals',
    'allow-pointer-lock',
    'allow-popups',
    'allow-popups-to-escape-sandbox',
    'allow-same-origin',
    'allow-scripts',
].join(' ')

const RESIZE_HANDLES = ['n', 's', 'e', 'w', 'nw', 'ne', 'sw', 'se']
const MOVE_THRESHOLD = 4
// Above the mobile profile button (z-index 99999) and below app dialogs (100010+).
const NFHS_ABOVE_CHROME_Z = 100000

function readerPaintZ (readerZ) {
    const n = Number(readerZ) || 0
    return NFHS_ABOVE_CHROME_Z + (Math.abs(n) % 8)
}

export default {
    name: 'NfhsDigitalReaderWindow',
    props: {
        reader: {
            type: Object,
            required: true,
        },
    },
    computed: {
        ...mapStores(useMainStore),
        eyebrow() {
            return this.reader.mode === 'preview' ? 'NFHS Digital Preview' : 'NFHS Digital Reader'
        },
        /** Collapsed readers render the dock instead of the panel, so the iframe unmounts. */
        isDocked() {
            return this.reader.mode === 'read' && Boolean(this.reader.collapsed)
        },
        readerUrl() {
            return nfhsOpenReaderUrl(this.reader)
        },
        title() {
            return nfhsReaderTitle(this.reader)
        },
        iframeTitle() {
            return this.reader.publication?.title
                || (this.reader.mode === 'read'
                    ? 'NFHS Digital publication reader'
                    : 'NFHS Digital publication preview')
        },
        panelStyle() {
            return {
                left: `${this.reader.left}px`,
                top: `${this.reader.top}px`,
                width: `${this.reader.width}px`,
                height: `${this.reader.height}px`,
                zIndex: readerPaintZ(this.reader.zIndex || NFHS_PANEL_Z_BASE),
            }
        },
        dockStyle() {
            const style = {
                top: `${this.reader.dockTop ?? this.reader.top}px`,
                width: `${NFHS_DOCK_WIDTH}px`,
                zIndex: readerPaintZ(this.reader.zIndex || NFHS_DOCK_Z_BASE) + 1,
            }
            if (this.reader.dockLeft != null) {
                style.left = `${this.reader.dockLeft}px`
                style.right = 'auto'
            } else {
                style.right = `${NFHS_INSET}px`
                style.left = 'auto'
            }
            return style
        },
        coverSrc() {
            return this.coverFailed ? NFHS_DEFAULT_COVER : nfhsReaderCover(this.reader)
        },
        isInteracting() {
            return this.isMoving || this.isResizing
        },
        iframeSandbox() {
            return NFHS_IFRAME_SANDBOX
        },
    },
    watch: {
        readerUrl: {
            immediate: true,
            handler(url) {
                this.historyPrimed = false
                this.frameReady = false
                this.frameSrc = url ? nfhsIframeHistorySeed(url) : ''
            },
        },
        isInteracting(active) {
            document.body.classList.toggle('nfhs-preview-interacting', active)
        },
        isDockDragging(active) {
            document.body.classList.toggle('nfhs-preview-interacting', active || this.isInteracting)
        },
        isDocked(docked) {
            if (docked || !this.readerUrl) return
            this.historyPrimed = false
            this.frameReady = false
            this.frameSrc = nfhsIframeHistorySeed(this.readerUrl)
        },
    },
    data() {
        return {
            resizeHandles: RESIZE_HANDLES,
            isMoving: false,
            isResizing: false,
            isDockDragging: false,
            coverFailed: false,
            moveStart: null,
            resizeStart: null,
            dockStart: null,
            dockDidMove: false,
            frameSrc: '',
            frameReady: false,
            historyPrimed: false,
        }
    },
    mounted() {
        window.addEventListener('pointermove', this.onPointerMove)
        window.addEventListener('pointerup', this.onPointerUp)
        window.addEventListener('pointercancel', this.onPointerUp)
        window.addEventListener('blur', this.onPointerUp)
        window.addEventListener('keydown', this.onKeydown)
        document.addEventListener('visibilitychange', this.onVisibilityChange)
        window.navigation?.addEventListener('navigate', this.onParentNavigate)
        window.addEventListener('message', this.onNfhsReaderMessage)
    },
    beforeUnmount() {
        this.clearInteraction()
        document.body.classList.remove('nfhs-preview-interacting')
        window.removeEventListener('pointermove', this.onPointerMove)
        window.removeEventListener('pointerup', this.onPointerUp)
        window.removeEventListener('pointercancel', this.onPointerUp)
        window.removeEventListener('blur', this.onPointerUp)
        window.removeEventListener('keydown', this.onKeydown)
        document.removeEventListener('visibilitychange', this.onVisibilityChange)
        window.navigation?.removeEventListener('navigate', this.onParentNavigate)
        window.removeEventListener('message', this.onNfhsReaderMessage)
    },
    methods: {
        onCoverError() {
            this.coverFailed = true
        },
        onNfhsReaderMessage(event) {
            const frame = this.$refs.nfhsFrame
            if (!frame || event.origin !== NFHS_READER_ORIGIN || event.source !== frame.contentWindow) return
            if (!isNfhsReaderMessage(event.data)) return
            if (event.data.type === 'ready' && (!this.mainStore.isLoggedIn || nfhsEmbedLogoutIsPending())) {
                postNfhsEmbedLogout(frame.contentWindow)
                clearNfhsEmbedLogoutPending()
                return
            }
            // Signing out inside the reader closes this panel. It does not sign them out of RefReps.
            if (event.data.type === 'signed-out') {
                this.mainStore.closeNfhsDigitalReader(this.reader.id)
            }
        },
        onIframeLoad() {
            if (!this.readerUrl) return
            if (!this.historyPrimed) {
                this.historyPrimed = true
                if (this.frameSrc !== this.readerUrl) {
                    this.frameSrc = this.readerUrl
                    return
                }
            }
            if (this.frameSrc === this.readerUrl) this.frameReady = true
        },
        onParentNavigate(event) {
            if (event.navigationType !== 'traverse' || !event.cancelable) return
            if (document.activeElement !== this.$refs.nfhsFrame) return
            event.preventDefault()
        },
        onPanelPointerDown() {
            this.mainStore.bringNfhsDigitalReaderToFront(this.reader.id)
        },
        onMoveStart(event) {
            if (event.button != null && event.button !== 0) return
            if (event.target.closest('button, a, input')) return
            this.mainStore.bringNfhsDigitalReaderToFront(this.reader.id)
            this.isMoving = true
            this.moveStart = {
                clientX: event.clientX,
                clientY: event.clientY,
                left: this.reader.left,
                top: this.reader.top,
            }
            event.currentTarget.setPointerCapture?.(event.pointerId)
        },
        onResizeStart(event, dir) {
            if (event.button != null && event.button !== 0) return
            this.mainStore.bringNfhsDigitalReaderToFront(this.reader.id)
            this.isResizing = true
            this.resizeStart = {
                dir,
                clientX: event.clientX,
                clientY: event.clientY,
                left: this.reader.left,
                top: this.reader.top,
                width: this.reader.width,
                height: this.reader.height,
            }
            event.currentTarget.setPointerCapture?.(event.pointerId)
        },
        onDockPointerDown(event) {
            if (event.button != null && event.button !== 0) return
            this.mainStore.bringNfhsDigitalReaderToFront(this.reader.id)
            this.isDockDragging = true
            this.dockDidMove = false
            const rect = event.currentTarget.getBoundingClientRect()
            this.dockStart = {
                clientX: event.clientX,
                clientY: event.clientY,
                left: rect.left,
                top: rect.top,
            }
            event.currentTarget.setPointerCapture?.(event.pointerId)
        },
        onPointerMove(event) {
            if (this.isMoving && this.moveStart) {
                this.mainStore.moveNfhsDigitalReader(
                    this.reader.id,
                    this.moveStart.left + (event.clientX - this.moveStart.clientX),
                    this.moveStart.top + (event.clientY - this.moveStart.clientY)
                )
                return
            }
            if (this.isResizing && this.resizeStart) {
                this.applyResize(event)
                return
            }
            if (this.isDockDragging && this.dockStart) {
                const dx = event.clientX - this.dockStart.clientX
                const dy = event.clientY - this.dockStart.clientY
                if (Math.abs(dx) > MOVE_THRESHOLD || Math.abs(dy) > MOVE_THRESHOLD) {
                    this.dockDidMove = true
                    this.mainStore.moveNfhsDigitalDock(
                        this.reader.id,
                        this.dockStart.left + dx,
                        this.dockStart.top + dy
                    )
                }
            }
        },
        applyResize(event) {
            const start = this.resizeStart
            const dx = event.clientX - start.clientX
            const dy = event.clientY - start.clientY
            const dir = start.dir
            let width = start.width
            let height = start.height
            let left = start.left
            let top = start.top
            if (dir.includes('e')) width = start.width + dx
            if (dir.includes('w')) {
                width = start.width - dx
                left = start.left + dx
            }
            if (dir.includes('s')) height = start.height + dy
            if (dir.includes('n')) {
                height = start.height - dy
                top = start.top + dy
            }
            this.mainStore.resizeNfhsDigitalReader(this.reader.id, { left, top, width, height })
        },
        onHeaderDoubleClick(event) {
            if (event.target.closest('button, a, input')) return
            this.mainStore.maximizeNfhsDigitalReader(this.reader.id)
        },
        onKeydown(event) {
            if (event.key !== 'Escape' || this.isDocked) return
            const top = [...(this.mainStore.nfhsDigital?.readers || [])]
                .filter((reader) => !reader.collapsed)
                .sort((a, b) => (b.zIndex || 0) - (a.zIndex || 0))[0]
            if (!top || top.id !== this.reader.id || !this.reader.maximized) return
            event.preventDefault()
            this.mainStore.maximizeNfhsDigitalReader(this.reader.id)
        },
        onPointerUp() {
            if (this.isDockDragging) {
                if (!this.dockDidMove) {
                    this.mainStore.expandNfhsDigitalReader(this.reader.id)
                }
            }
            this.clearInteraction()
        },
        onVisibilityChange() {
            if (document.hidden) {
                this.clearInteraction()
            }
        },
        clearInteraction() {
            this.isMoving = false
            this.isResizing = false
            this.isDockDragging = false
            this.moveStart = null
            this.resizeStart = null
            this.dockStart = null
            this.dockDidMove = false
        },
    },
}
</script>

<style>
.nfhs-preview-panel,
.nfhs-reader-dock-wrap {
    z-index: 100400 !important;
    transform: translate3d(0, 0, 0);
    -webkit-transform: translate3d(0, 0, 0);
}
.nfhs-preview-panel {
    isolation: isolate;
}
body.nfhs-preview-interacting {
    cursor: grabbing !important;
    user-select: none !important;
}
.nfhs-preview-panel--compact {
    box-shadow: 0 22px 60px rgba(15, 23, 42, 0.32);
}
.nfhs-preview-panel--maximized {
    box-shadow: 0 8px 40px rgba(15, 23, 42, 0.28);
}
@media (max-width: 900px) {
    .nfhs-preview-panel {
        border-radius: 16px;
        padding-top: env(safe-area-inset-top, 0px);
        padding-bottom: env(safe-area-inset-bottom, 0px);
    }
}
.nfhs-reader-iframe {
    display: block;
    transform: translateZ(0);
    backface-visibility: hidden;
}
.nfhs-preview-interaction-shield {
    background: transparent;
    touch-action: none;
}
.nfhs-preview-resize-handle {
    position: absolute;
    z-index: 4;
    touch-action: none;
}
.nfhs-preview-resize-handle--n {
    top: 0;
    left: 16px;
    right: 16px;
    height: 10px;
    cursor: ns-resize;
}
.nfhs-preview-resize-handle--s {
    left: 16px;
    right: 16px;
    bottom: 0;
    height: 10px;
    cursor: ns-resize;
}
.nfhs-preview-resize-handle--e {
    top: 16px;
    right: 0;
    bottom: 16px;
    width: 10px;
    cursor: ew-resize;
}
.nfhs-preview-resize-handle--w {
    top: 16px;
    left: 0;
    bottom: 16px;
    width: 10px;
    cursor: ew-resize;
}
.nfhs-preview-resize-handle--nw,
.nfhs-preview-resize-handle--ne,
.nfhs-preview-resize-handle--sw,
.nfhs-preview-resize-handle--se {
    width: 22px;
    height: 22px;
}
.nfhs-preview-resize-handle--nw { top: 0; left: 0; cursor: nwse-resize; }
.nfhs-preview-resize-handle--ne { top: 0; right: 0; cursor: nesw-resize; }
.nfhs-preview-resize-handle--sw { left: 0; bottom: 0; cursor: nesw-resize; }
.nfhs-preview-resize-handle--se { right: 0; bottom: 0; cursor: nwse-resize; }
.nfhs-preview-resize-handle--nw::after,
.nfhs-preview-resize-handle--ne::after,
.nfhs-preview-resize-handle--sw::after,
.nfhs-preview-resize-handle--se::after {
    content: '';
    position: absolute;
    width: 11px;
    height: 11px;
    border-color: #94a3b8;
    border-style: solid;
}
.nfhs-preview-resize-handle--nw::after { top: 5px; left: 5px; border-width: 2px 0 0 2px; }
.nfhs-preview-resize-handle--ne::after { top: 5px; right: 5px; border-width: 2px 2px 0 0; }
.nfhs-preview-resize-handle--sw::after { bottom: 5px; left: 5px; border-width: 0 0 2px 2px; }
.nfhs-preview-resize-handle--se::after { bottom: 5px; right: 5px; border-width: 0 2px 2px 0; }
.nfhs-reader-dock-wrap {
    width: 80px;
}
.nfhs-reader-dock {
    z-index: 1;
    cursor: grab;
    touch-action: none;
    transition: box-shadow 160ms ease, transform 160ms ease;
}
.nfhs-reader-dock:active { cursor: grabbing; }
.nfhs-reader-dock--floating {
    box-shadow: 0 22px 40px rgba(15, 23, 42, 0.28), 0 0 0 2px rgba(20, 184, 166, 0.35);
    transform: translate3d(0, 0, 0) scale(1.06) rotate(-2deg);
    -webkit-transform: translate3d(0, 0, 0) scale(1.06) rotate(-2deg);
    cursor: grabbing;
}
.nfhs-reader-dock-close {
    position: absolute;
    top: -6px;
    right: -6px;
    z-index: 220;
    display: flex;
    height: 22px;
    width: 22px;
    align-items: center;
    justify-content: center;
    border: 0;
    border-radius: 999px;
    background: #0f172a;
    color: #fff;
    box-shadow: 0 2px 8px rgba(15, 23, 42, 0.28);
    cursor: pointer;
    transition: background-color 120ms ease, transform 120ms ease;
}
.nfhs-reader-dock-close:hover {
    background: #dc2626;
    transform: scale(1.06);
}
</style>
